import {
  ProjectResearchSubjectDetail,
  SubjectScheduleGroupDetail,
} from 'src/app/services/models/subject-schedules/subject-schedule.model';

export type SubjectManagementDetailsSourceType = 'subject_schedule' | 'project_research_subject';

export interface SubjectManagementDetailsSectionState {
  id: number;
  sourceType: SubjectManagementDetailsSourceType;
  status: string;
  editable: boolean;
  teacherManagementEnabled: boolean;
}

export function canEditDetailsSection(): boolean {
  return true;
}

function createSectionState(
  section: SubjectScheduleGroupDetail | ProjectResearchSubjectDetail,
  sourceType: SubjectManagementDetailsSourceType,
): SubjectManagementDetailsSectionState {
  const status = String(section.status ?? '').trim();

  return {
    id: Number(section.id),
    sourceType,
    status,
    editable: canEditDetailsSection(),
    teacherManagementEnabled: true,
  };
}

export function buildDetailsSectionStates(
  subjectSchedules: SubjectScheduleGroupDetail[],
  projectResearchSubjects: ProjectResearchSubjectDetail[],
): SubjectManagementDetailsSectionState[] {
  return [
    ...subjectSchedules.map((section) => createSectionState(section, 'subject_schedule')),
    ...projectResearchSubjects.map((section) => createSectionState(section, 'project_research_subject')),
  ];
}

export function buildSelectedTeachingDateLabel(
  sequenceDay: string | null | undefined,
  fallbackLabel: string,
  formattedDate: string,
): string {
  const normalizedSequenceDay = sequenceDay?.trim();
  const label = normalizedSequenceDay != null && normalizedSequenceDay !== ''
    ? normalizedSequenceDay
    : fallbackLabel;

  return `${label} - ${formattedDate}`;
}

function parseErrorResponse(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function getFirstErrorText(...values: unknown[]): string {
  return values
    .find((value): value is string => typeof value === 'string' && value.trim() !== '')
    ?.trim() ?? '';
}

export function getSubjectManagementDetailsApiErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  const responseError = parseErrorResponse(
    (error as { error?: unknown } | null | undefined)?.error ?? error
  );

  if (responseError != null && typeof responseError === 'object') {
    const errorRecord = responseError as { errors?: unknown; error?: unknown; message?: unknown };
    const errors = Array.isArray(errorRecord.errors)
      ? errorRecord.errors
        .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
        .map((item) => item.trim())
      : [];

    if (errors.length > 0) {
      return errors.join('\n');
    }

    return getFirstErrorText(errorRecord.error, errorRecord.message, fallbackMessage);
  }

  return getFirstErrorText(responseError, (error as { message?: unknown } | null | undefined)?.message, fallbackMessage);
}
